/**
 * LeftShield Portal - Governance Posture Page
 *
 * F044 Verdict, Voice & Score Architecture — the single decomposition page for the ONE composite
 * Governance Score. Every other surface that wants to show "how governed is this tenant" links here
 * rather than recomputing or re-presenting its own score (F044 §1.1 core capability #6). F045 (Object-
 * Based IA) explicitly keeps this route (`/governance-posture`) alive specifically to host this
 * decomposition, rather than folding it into Board View — see F045-PROGRESS.md "Compliance posture x3".
 *
 * DELTA FROM THE PRE-F044 VERSION OF THIS PAGE: this page previously rendered its OWN "five weighted
 * dimensions" (Runtime Coverage / Control Coverage / Enforcement Strength / Control Effectiveness /
 * Evidence Integrity) sourced from a separate `governanceApi.getPosture()` call with hardcoded
 * magnitude-to-colour thresholds (`getScoreColor`/`getRiskColor`). That was exactly the "9+ scattered
 * scores, each page invents its own scale" defect F044 exists to close (F044 Executive Summary). This
 * page now reads the ONE composite (`governanceScoreApi.getScore()`) and its ONE decomposition
 * (`governanceScoreApi.getDecomposition()`) — computed in exactly one place, `GovernanceScoreComposer`
 * — and renders exclusively through the shared verdict-kit components (`ScoreCard`, `StatTile`,
 * `EmptyVerdict`, `ErrorState`). No page-invented colour thresholds: a per-dimension value is real,
 * already-normalised data with no independent "is this good or bad" business-rule backing it at this
 * granularity, so — consistent with `<ScoreCard>` itself, which renders the composite with no magnitude
 * colouring either — dimension values are shown plainly. The only colour this page assigns is the
 * `Neutral` used for a dimension with no signal (see `isMeasured` below), never a fabricated
 * pass/fail judgement.
 *
 * Route: /governance-posture | Nav: Evidence & Assurance > Governance Posture
 */
import type { FC, ReactNode } from 'react';
import {
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Spinner,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { GaugeRegular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { governanceScoreApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyVerdict, ErrorState, ScoreCard, StatTile } from '../components/verdict';
import type { GovernanceScore, ScoreDecomposition, ScoreDimension, Verdict } from '../types';

// ---------------------------------------------------------------------------
// Type for the shape `fetchWithAuth` throws on a non-OK response (see `services/api.ts`) — a plain
// `Error` augmented with the HTTP `status`. Narrowed via `instanceof Error` before use, matching the
// convention already established in `TrustPage.tsx`.
// ---------------------------------------------------------------------------

interface ApiErrorLike extends Error {
  status?: number;
}

function isApiErrorLike(err: unknown): err is ApiErrorLike {
  return err instanceof Error;
}

/** `GET /portal/governance-score` 404s when nothing has ever been computed/snapshotted for this
 *  tenant yet — an expected "not yet computed" outcome, not a fetch failure. */
function isNotComputedYet(err: unknown): boolean {
  return isApiErrorLike(err) && err.status === 404;
}

const NOT_SCOREABLE_ACTION = { label: 'Connect an Integration', route: '/integrations' };

const useStyles = makeStyles({
  weightCaption: {
    color: tokens.colorNeutralForeground3,
  },
  notMeasuredTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
  },
  notMeasuredLabel: {
    color: tokens.colorNeutralForeground3,
  },
  notMeasuredValue: {
    color: tokens.colorNeutralForeground3,
  },
});

/** One dimension cell — a real, measured value renders through `<StatTile>`; a dimension the
 *  gatherer had no signal for (F044 owner decision #2 — see `ScoreInputGatherer` XML docs on the API
 *  side) reads explicitly as "Not Measured," never as a fabricated zero/failing score. */
const DimensionTile: FC<{ dimension: ScoreDimension }> = ({ dimension }) => {
  const styles = useStyles();
  const ps = usePageStyles();

  return (
    <div className={ps.statTile}>
      {dimension.isMeasured ? (
        <StatTile label={dimension.label} value={Math.round(dimension.value)} colorIntent="Neutral" />
      ) : (
        <div className={styles.notMeasuredTile}>
          <Caption1 className={styles.notMeasuredLabel}>{dimension.label}</Caption1>
          <Body1 className={styles.notMeasuredValue}>Not Measured</Body1>
        </div>
      )}
      <Caption1 className={styles.weightCaption}>
        Weight {Math.round(dimension.weight * 100)}% &middot; Source {dimension.sourceFeature}
      </Caption1>
    </div>
  );
};

const NOT_SCOREABLE_VERDICT: Verdict = {
  kind: 'State',
  label: 'Not Scoreable',
  colorIntent: 'Neutral',
  status: 'NOT SCOREABLE',
  why: 'The Governance Score requires an active tenant with governed data — a non-zero denominator (F043 guard).',
  impact: 'No composite score or dimension breakdown can be shown until governed data exists for this tenant.',
  action: NOT_SCOREABLE_ACTION,
};

export const GovernancePosturePage: FC = () => {
  const ps = usePageStyles();

  const scoreQuery = useQuery<GovernanceScore, ApiErrorLike>({
    queryKey: ['governance-score'],
    queryFn: governanceScoreApi.getScore,
    retry: (failureCount, err) => !isNotComputedYet(err) && failureCount < 2,
  });

  const decompositionQuery = useQuery<ScoreDecomposition, ApiErrorLike>({
    queryKey: ['governance-score', 'decomposition'],
    queryFn: governanceScoreApi.getDecomposition,
  });

  let body: ReactNode;

  if (decompositionQuery.isLoading) {
    body = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading governance posture..." />
      </div>
    );
  } else if (decompositionQuery.isError) {
    body = (
      <ErrorState
        cause="Unable to load the governance score decomposition."
        retryable
        onRetry={() => decompositionQuery.refetch()}
      />
    );
  } else {
    const decomposition = decompositionQuery.data;

    if (!decomposition || !decomposition.isScoreable) {
      body = <EmptyVerdict verdict={NOT_SCOREABLE_VERDICT} />;
    } else {
      const notYetComputed = isNotComputedYet(scoreQuery.error);
      const scoreForCard: GovernanceScore = scoreQuery.data ?? {
        tenantId: 0,
        composite: undefined,
        isScoreable: false,
        weightProfileId: '',
        computedAt: '',
      };

      body = (
        <>
          {scoreQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
              <Spinner label="Loading the composite score..." />
            </div>
          ) : scoreQuery.isError && !notYetComputed ? (
            <ErrorState
              cause="Unable to load the composite Governance Score."
              retryable
              onRetry={() => scoreQuery.refetch()}
            />
          ) : (
            <ScoreCard
              score={scoreForCard}
              decompositionRoute="/governance-posture"
              notScoreableAction={NOT_SCOREABLE_ACTION}
            />
          )}

          <Card>
            <CardHeader
              header={<Title2>Score Decomposition</Title2>}
              description={
                <Body2>
                  The {decomposition.dimensions.length} weighted dimensions that compose the Governance Score —
                  decomposed in exactly one place, referenced everywhere else.
                </Body2>
              }
            />
            <div className={ps.cardBody}>
              <div className={ps.statGrid}>
                {decomposition.dimensions.map((dimension) => (
                  <DimensionTile key={dimension.key} dimension={dimension} />
                ))}
              </div>
            </div>
          </Card>
        </>
      );
    }
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="Governance Posture"
        subtitle="The single decomposition of the ONE composite Governance Score — weighted dimensions, computed in exactly one place"
        icon={<GaugeRegular />}
      />
      {body}
    </div>
  );
};
